import Backbone from 'backbone'
import template from './period-selector.hbs'
import calendars from 'scoreboard/components/calendars'
import moment from 'moment'
import globals from 'scoreboard/components/globals'
import _ from 'lodash'
import calendarInfo, {
	getYearMatches,
	NO_YEARS,
	YEARS_BY_DATE
} from './calendar-info'
import selects from 'scoreboard/components/selects'
import i18n from 'scoreboard/components/i18n'
import Dataset from 'scoreboard/datasets/model/dataset'
const { ALL_TIME } = Dataset

/*
 * Selector that handles showing periods for a given calendar.
 *
 * Usage:
 * <pre>
 * var periodSelector = new PeriodSelector({
 *     periodId: 123, // optional (can specify calendarId instead)
 *     calendarId: 12, // optional (can specify periodId instead)
 *     idPrefix: 'prefix', // so you can attach a label to the period dropdown
 *     name: 'name', // optional
 *     backToDefaultButton: true //optional
 * })
 *
 * // IMPORTANT: must be in the DOM before rendering
 * this.$('.somewhere').append(periodSelector.el)
 * periodSelector.render()
 *
 * // to change the displayed calendar
 * periodSelector.setCalendar(123)
 *
 * // to change the displayed period
 * periodSelector.setPeriod(1, { silent: true }) // can omit the 2nd argument
 * periodSelector.val(1) // same thing
 *
 * // to return the currently selected calendar period
 * var periodId = periodSelector.val()
 *
 * // to listen to changes
 * periodSelector.on('period', function(periodId) { console.log(periodId); })
 * </pre>
 */
var PeriodSelector = Backbone.View.extend({

	className: 'period-selector content',

	events: {
		'change .period': '_changePeriod',
		'change .year': '_changeYear',
		'click .back-to-default-period': '_backToDefaultPeriod'
	},

	initialize: function(options) {
		this.options = options || {}
		//can derive calendar to show from just passing a period, if both
		//a period and calendar id are specified the calendar used is the one
		//specified by the calendarId
		var pid = (options && options.periodId) || globals.periodId
		var calPeriod = calendars.periodsById[pid] || calendars.periodsById[globals.periodId]
		this.periodId = calPeriod.id
		this.calendarId = (options && options.calendarId) || calPeriod.calendar.id
		this.idPrefix = options ? options.idPrefix : null
	},

	render: function() {
		let context = {
			idPrefix: this.idPrefix
		}
		if (this.options.backToDefaultButton) {
			this.defaultPeriodId = calendars.defaultPeriods[this.calendarId]
			context.defaultPeriodId = this.defaultPeriodId
			context.defaultPeriodText = i18n.get('common.calendar.period.backToDefaultPeriod', calendars.periodsById[this.defaultPeriodId].label)
		}
		this.$el.html(template(context))

		// so form-helper can work with this
		if (this.options.name) {
			this.$el.attr('name', this.options.name)
		}
		this.$el.data('view', this)

		this.setCalendar()
		this.backToDefaultButton = this.$('.back-to-default-period')
		this.toggleBackToDefaultPeriodButton(this.selectedPeriodId)
		selects(this.$('select'))

		return this
	},

	yearDropdownWrapper: function () {
		return this.$('.year-wrapper')
	},

	yearDropdown: function () {
		return this.yearDropdownWrapper().find('select')
	},

	periodDropdownWrapper: function () {
		return this.$('.period-wrapper')
	},

	periodDropdown: function () {
		return this.periodDropdownWrapper().find('select')
	},

	// get or set the ID of the selected period
	val: function(periodId, options) {
		if (periodId) {
			this.setPeriod(periodId, options)
			return
		}

		var id = this.periodDropdown().val()
		return id ? parseInt(id) : null
	},

	//toggles the display of this view
	toggle: function() {
		this.$el.toggle.apply(this.$el, arguments)
	},

	//sets the period and year dropdowns based on the a given calendar id
	//can be called with an object with id or by the id itself, no arg uses
	//it's currently set calendar id which defaults to the global calendar id
	setCalendar: function(calendar) {
		var id = _.result(calendar, 'id', (calendar || this.calendarId))

		if (id == ALL_TIME) {
			// just don't bother changing anything; toggler-rows will handle
			// hiding the period selectors when All Time is selected
			return
		}

		var calendar = calendars.calendarsById[id]
		var info = calendarInfo(calendar)
		this.calendarId = id

		// the calendar corresponding to the global period
		var currentCalendar = this.periodId && calendars.periodsById[this.periodId].calendar

		// determine selected period
		if (currentCalendar && currentCalendar.id == id) {
			this.selectedPeriodId = this.periodId
		} else {
			this.selectedPeriodId = calendars.defaultPeriods[id]
		}
		var selectedPeriod = calendars.periodsById[this.selectedPeriodId]

		// render period and year dropdowns, as appropriate
		this.$el.removeClass('is-extended')
		if (info.style == NO_YEARS) {
			this.yearDropdownWrapper().hide()
			this.renderPeriods(calendar.periods)
			this.$el.addClass('is-extended')
		} else {
			this.yearDropdownWrapper().show()
			this.yearDropdown().empty()
			this.renderYears(Object.keys(info.periodsByYear))

			var year
			if (info.style == YEARS_BY_DATE) {
				year = moment(selectedPeriod.end).year()
			} else {
				year = getYearMatches(selectedPeriod.label)[0]
			}

			this.renderPeriodsForYear(year)
			// record the previous year
			this.previousYear = year
		}

		// select correct values in the dropdowns
		this.periodDropdown().val(this.selectedPeriodId)
		// if the period dropdown doesn't have a value (which will happen if
		// the year dropdown isn't set for the current year)
		if (!this.periodDropdown().val()) {
			// select the first option
			this.periodDropdown().val(this.$('select.period option')[0].value)
		}
		// make sure the scoreboard-select display is correct.  we can't call
		// .change b/c we don't want an event to fire
		selects(this.$('select'))
	},

	renderPeriodsForYear: function(year) {
		var calendar = calendars.calendarsById[this.calendarId]
		var info = calendarInfo(calendar)
		var periods = info.periodsByYear[year]

		// render periods dropdown
		this.periodDropdown().empty()
		_.each(periods, function(period) {
			var label = info.labelsByPeriod[period.id]
			this.periodDropdown().append(new Option(label, period.id))
		}.bind(this))

		// change year dropdown
		this.yearDropdown().val(year)
	},

	// renders the given periods in the period dropdown
	renderPeriods: function(periods) {
		this.periodDropdown().empty()
		_.each(periods, function(period) {
			this.periodDropdown().append(new Option(period.label, period.id))
		}.bind(this))
	},

	// renders the given years in the year dropdown
	renderYears: function(years) {
		_.each(years, function(year) {
			this.yearDropdown().append(new Option(year, year))
		}.bind(this))
	},

	// external API method for changing the selected period. Handles updating
	// the dropdowns if the year changed
	setPeriod: function (id, options) {
		var period = calendars.periodsById[id]
		var info = calendarInfo(period.calendar)
		if (info.style != NO_YEARS) {
			var year = info.style == YEARS_BY_DATE ?
				period.start.get('year') : getYearMatches(period.label)[0]

			// If the year dropdown is applicable (not "is-extended"/hidden) and
			// the year has changed, we need to repopulate the period dropdown
			if (!this.$el.hasClass('is-extended') && year != this.yearDropdown().val()) {
				this.renderPeriodsForYear(year)
				this.yearDropdown().val(year)
			}
		}

		this._changePeriod(id, options)
	},

	// executed when the period dropdown changes
	_changePeriod: function(e, options) {
		var id = _.isNumber(e) || _.isString(e) ? e : this.$(e.target).val()
		this.selectedPeriodId = id
		this.toggleBackToDefaultPeriodButton(id)
		this.periodDropdown().val(id)
		selects(this.$('select'))
		if (!_.result(options, 'silent')) {
			this.trigger('period', id)
		}
	},

	// adjusts the periods dropdown when the year is changed
	_changeYear: function(e) {
		var year = _.isNumber(e) ? e : this.$(e.target).val()
		var calendar = calendars.calendarsById[this.calendarId]
		var info = calendarInfo(calendar)
		var periods = info.periodsByYear[year]
		this.renderPeriodsForYear(year)

		// change year dropdown
		this.yearDropdown().val(year)

		// change the period dropdown by trying to match the period from the new
		// year to the period of the old year based on period end dates
		if (this.previousYear) {
			var previousPeriod = calendars.periodsById[this.selectedPeriodId]
			// need to do it this way because when the style is YEARS_BY_LABEL
			// the year based on the start/end dates doesn't necessarily match
			// the year based on the period's label, e.g. when dealing with
			// the fiscal quarterly calendar.
			var previousEnd = moment(previousPeriod.end)
			previousEnd.add(year - this.previousYear, 'years')
			var selectedPeriod = _.findLast(periods, function(period) {
				var periodEnd = moment(period.end)
				// if periodEnd is feb 29, subtract a day to avoid problems with
				// leap years
				if (periodEnd.month() == 1 && periodEnd.date() == 29) {
					periodEnd.date(28)
				}
				return this.isSameDate(periodEnd, previousEnd)
			}.bind(this))
			if (selectedPeriod == null) {
				selectedPeriod = parseInt(this.periodDropdown()[0].options[0].value)
			}
			this._changePeriod(selectedPeriod.id || selectedPeriod)
		}

		// record the previous year
		this.previousYear = year
		this.trigger('year', year)
	},

	/**
	 * This function checks if the dates are the same or not.
	 * @param periodEnd
	 * 			date object representing the end date of a period
	 * @param previousEnd
	 * 			date object representing the previous end date for comparison
	 * @returns {boolean}
	 * 			True if both dates share the same month and day, false otherwise
	 */
	isSameDate: function (periodEnd, previousEnd) {
		// #46305: isBefore() & isSame() were not accurate in all
		// situations. This prevents the month from incorrectly
		// changing when the year is updated
		return periodEnd.month() === previousEnd.month()
			&& periodEnd.date() === previousEnd.date()
	},

	_backToDefaultPeriod: function() {
		this.setPeriod(this.defaultPeriodId)
		this.trigger('backToDefaultDate')
	},

	toggleBackToDefaultPeriodButton: function(id) {
		let periodId = id || globals.periodId
		let disableButtons = this.backToDefaultButton.data('id') === parseInt(periodId)
		this.backToDefaultButton.toggleClass('is-disabled', disableButtons)
	}

})

export default PeriodSelector

PeriodSelector.__filename__ = 'components/period-selector.js'
